import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import {
  getOfflineQueueCount,
  listOfflineQueue,
  removeOfflineEntry,
} from "@/lib/offlinePosQueue";
import { toast } from "sonner";

async function replayPayment(entry) {
  const { billId, branchId, method, tendered } = entry;
  const base = `/bills/${billId}/payments`;
  const params = { branch_id: branchId };

  if (method === "cash") {
    const { data } = await api.post(`${base}/cash`, { tendered_amount: tendered }, { params });
    return data;
  }
  if (method === "card") {
    const { data } = await api.post(`${base}/card`, {}, { params });
    return data;
  }
  if (method === "credit") {
    const { data } = await api.post(`${base}/credit`, { customer_id: entry.customerId }, { params });
    return data;
  }
  const { data } = await api.post(
    `${base}/retailer`,
    { customer_id: entry.customerId },
    { params },
  );
  return data;
}

export function useOfflinePosSync({ enabled = true, onSynced } = {}) {
  const [pending, setPending] = useState(() => getOfflineQueueCount());
  const [syncing, setSyncing] = useState(false);

  const refreshCount = useCallback(() => {
    setPending(getOfflineQueueCount());
  }, []);

  const syncQueue = useCallback(async () => {
    if (syncing || !navigator.onLine) return { synced: 0 };
    const queue = listOfflineQueue();
    if (!queue.length) return { synced: 0 };

    setSyncing(true);
    let synced = 0;
    const failures = [];

    for (const entry of queue) {
      try {
        const data = await replayPayment(entry);
        removeOfflineEntry(entry.id);
        synced += 1;
        onSynced?.(data, entry);
      } catch (err) {
        failures.push(entry.id);
        if (err.response?.status === 404 || err.response?.status === 422) {
          removeOfflineEntry(entry.id);
        }
      }
    }

    refreshCount();
    setSyncing(false);

    if (synced > 0) {
      toast.success(`Synced ${synced} offline payment${synced > 1 ? "s" : ""}`);
    }
    if (failures.length && synced === 0) {
      toast.error("Some offline payments could not sync — check POS");
    }

    return { synced };
  }, [onSynced, refreshCount, syncing]);

  useEffect(() => {
    if (!enabled) return undefined;

    const onOnline = () => {
      refreshCount();
      syncQueue();
    };

    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [enabled, refreshCount, syncQueue]);

  return { pending, syncing, syncQueue, refreshCount };
}
